// src/pages/Blogs.jsx
import { X } from "lucide-react";
import React, { useState, useEffect } from "react";
import blogs from "../assets/blogsData/blogsData";
import { motion, AnimatePresence } from "framer-motion";

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const gridVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { staggerChildren: 0.05, duration: 0.4 } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.3 } },
};

const Blogs = () => {
  const [activeImages, setActiveImages] = useState(null);
  const [isTouch, setIsTouch] = useState(false);

  // Detect touch devices
  useEffect(() => {
    const handleTouch = () => setIsTouch(true);
    window.addEventListener("touchstart", handleTouch, { once: true });
    return () => window.removeEventListener("touchstart", handleTouch);
  }, []);

  return (
    <>
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold">
              The latest blog from us.
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg md:text-xl mt-4">
              Stay informed with our milestones, certifications, and innovations
              as we continue to deliver precision fasteners and machined
              components trusted by the automotive, agriculture, and aerospace
              industries.
            </p>
          </div>

          {/* Blogs grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog, i) => (
              <div
                key={i}
                className="relative cursor-pointer rounded-xl overflow-hidden"
                onClick={() => setActiveImages(blog.images)} // open popup on click
              >
                <div className="relative rounded-xl aspect-[4/3] overflow-hidden">
                  <img
                    src={blog.images[0]}
                    alt={blog.title}
                    className="w-full h-full object-cover rounded-xl transition-transform duration-300 hover:scale-105"
                  />
                </div>

                <p className="text-black text-sm font-light sm:text-base mt-4">
                  {blog.time}
                </p>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold mt-2">
                  {blog.title}
                </h3>
                <p className="text-[#4a4f54] text-sm sm:text-base mt-1">
                  {blog.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fullscreen overlay */}
      <AnimatePresence>
        {activeImages && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex flex-col items-center justify-center p-6 overflow-auto"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={overlayVariants}
          >
            {/* Close Button */}
            <motion.button
              className="absolute top-6 right-6 z-[120] w-12 h-12 flex items-center justify-center rounded-full bg-red-700 text-white text-2xl sm:text-3xl shadow-lg"
              onClick={() => setActiveImages(null)}
              initial={{ scale: 0 }}
              animate={{ scale: 1, transition: { duration: 0.3 } }}
              exit={{ scale: 0, transition: { duration: 0.2 } }}
            >
              <X />
            </motion.button>

            {/* Grid Images */}
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-6xl"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={gridVariants}
            >
              {activeImages.map((src, idx) => (
                <motion.img
                  key={idx}
                  src={src}
                  alt={`popup-${idx}`}
                  className="w-full h-80 object-contain rounded-lg shadow-lg bg-white p-2"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Blogs;
